import { useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@/shared/components';

/**
 * Zwei-Faktor ausschalten (Board „Kontosicherheit“): eigener Bestätigungsdialog mit Passwort und
 * Code, damit es nicht neben „Neue Codes“ aus Versehen passiert. Fehler stehen im Dialog.
 */
export function DisableTwoFactorDialog({
  open,
  onOpenChange,
  onDisable,
  failure,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisable: (password: string, code: string) => Promise<void>;
  failure: string | null;
}) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || !code) return;
    setBusy(true);
    try {
      await onDisable(password, code);
    } finally {
      setBusy(false);
    }
  };
  // Während des Ausschaltens bleibt der Dialog offen: sonst landete das Ergebnis ungesehen in ihm.
  const change = (next: boolean) => {
    if (busy) return;
    if (!next) {
      setPassword('');
      setCode('');
    }
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>Zwei-Faktor ausschalten?</DialogTitle>
            <DialogDescription>
              Danach reicht dein Passwort zum Anmelden; angemeldete Geräte werden abgemeldet.
              Verlangt dein Workspace Zwei-Faktor, richtest du sie bei der nächsten Anmeldung neu
              ein.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            {failure && (
              <p
                role="alert"
                className="bg-sealwax-tint text-sealwax rounded-control p-3 text-sm font-semibold"
              >
                {failure}
              </p>
            )}
            <FormField label="Aktuelles Passwort" required>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoFocus
              />
            </FormField>
            <FormField label="Code aus der Authenticator-App" required>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              onClick={() => change(false)}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="min-h-11"
              loading={busy}
              disabled={!password || !code}
            >
              Ausschalten
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
